import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchLatestReadings, fetchLiveDeviceReading } from "../actions/liveMonitorActions";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import PowerQualitySvg from "../components/generatorDiagram/PowerQualitySvg";
import MetricCard from "../components/generatorDiagram/MetricCard";
import { ArrowLeft } from "lucide-react";

function DeviceDetailsPage() {
    const { deviceId } = useParams();
    const dispatch = useDispatch();

    const navigate = useNavigate();
    const location = useLocation();

    const navigateBack = () => {
        const segments = location.pathname.split("/").filter(Boolean);
        navigate(`/${segments[0]}`)
    }

    // const [activePage, setActivePage] = useState("live");

    // useEffect(() => {
    
    //         dispatch(fetchLatestReadings());
    
    //         const interval = setInterval(() => {
    //             dispatch(fetchLatestReadings());
    //         }, 60000);
    
    //         return () => clearInterval(interval);
    //     }, [dispatch]);

    useEffect(() => {
        if (!deviceId) return;

        const loadReading = () => {
            dispatch(fetchLiveDeviceReading(deviceId));
        }

        loadReading();

        const interval = setInterval(
            loadReading,
            30000
        );

        return () => clearInterval(interval)
    }, [dispatch, deviceId])

    // const readings = useSelector(
    //     (state) => state.liveMonitor.readings || []
    // );

    // // The reducer returns readings as objects; convert it into an array to find the device
    // const readingsArray = Object.values(readings || {});

    // const deviceReading = readingsArray.find(
    //     (r) => r.DeviceId === deviceId
    // );

    const deviceReading = useSelector(
        (state) => state.liveMonitor.readings[deviceId]
    )

    if (!deviceReading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p>Loading device...</p>
            </div>
        )
    }

    const {
        DeviceId,
        DiviceName,
        Ddate,
        Dtime,
        VoltageAB,
        VoltageBC,
        VoltageCA,
        VoltageAN,
        VoltageBN,
        VoltageCN,
        CurrentA,
        CurrentB,
        CurrentC,
        CurrentN,
        TotalActivePowerKW,
        TotalReactivePowerKVAR,
        TotalApparentPowerKVA,
        PowerFactor,
        FrequencyHz,
        ActiveEnergyKWh,
        ReactiveEnergyKVARh
    } = deviceReading;

    const vAB = Number(VoltageAB);
    const vBC = Number(VoltageBC);
    const vCA = Number(VoltageCA);

    const cA = Number(CurrentA);
    const cB = Number(CurrentB);
    const cC = Number(CurrentC);

    const voltageAverage = (vAB + vBC + vCA) / 3;
    const currentAverage = (cA + cB + cC) / 3;

    const voltageUnbalanceWorst = 
        voltageAverage === 0 
        ? 0 
        : Math.max(
            Math.abs(vAB - voltageAverage),
            Math.abs(vBC - voltageAverage),
            Math.abs(vCA - voltageAverage)
        ) / voltageAverage * 100;
    ;

    const currentUnbalanceWorst =
        currentAverage === 0
        ? 0 
        : Math.max(
        Math.abs(cA - currentAverage),
        Math.abs(cB - currentAverage),
        Math.abs(cC - currentAverage)
    ) / currentAverage * 100;
    
    return (
        // <DashboardLayout
        //     role="A"
        //     activePage={activePage}
        //     setActivePage={setActivePage}
        // >
        <div className="flex flex-col gap-3">
            <div className="bg-white rounded-3xl px-4 md:px-8 py-4 md:py-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-6">
                    {/* Left Side */}
                    <div>
                        <button
                            onClick={navigateBack}
                            className="flex items-center gap-2 text-textSecondary hover:text-textPrimary transition"
                        >
                            <ArrowLeft size={16} />
                            <span className="text-sm font-medium">
                                Back to Live Monitor
                            </span>
                        </button>

                        <div className="mt-4">
                            <p className="text-xs uppercase tracking-[4px] text-electricBlue font-semibold">
                                Device Monitoring
                            </p>

                            <h1 className="mt-2 text-3xl md:text-4xl font-bold text-textPrimary">
                                {DiviceName || DeviceId}
                            </h1>

                            <p className="mt-2 text-textSecondary">
                                Real-time power quality and electrical measurements
                            </p>
                        </div>
                    </div>

                    {/* Right Side */}
                    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">

                        {/* Live Status */}
                        <div className="md:text-right">
            
                            <p className="mt-2 text-xs uppercase tracking-[2px] text-textSecondary">
                                Last update
                            </p>

                            <p className="w-full md:w-auto text-sm font-semibold text-textPrimary">
                                {Ddate} {Dtime}
                            </p>
                        </div>

                        {/* History Button */}
                        <button 
                            onClick={() => navigate(`${location.pathname}/history`)}
                            className="px-5 py-3 rounded-xl bg-deepNavy text-white hover:bg-navy transition"
                        >
                            View History
                        </button>
                    </div>
                </div>
            </div>

            <div className="md:hidden">
                <div className="mb-2 text-xs text-textSecondary p-6">
                    Rotate to landscape and swipe horizontally to explore the diagram
                </div>
            </div>

            <div className="bg-white rounded-xl overflow-auto max-w-full">
                <div className="relative h-[900px] w-[1400px]">
                    <PowerQualitySvg />

                    <MetricCard
                        title="Voltage BC"
                        value={VoltageBC}
                        unit="V"
                        className="left-[175px] top-[100px]"
                    />

                    <MetricCard
                        title="Voltage AB"
                        value={VoltageAB}
                        unit="V"
                        className="left-[175px] top-[300px]"
                    />

                    <MetricCard
                        title="Voltage CA"
                        value={VoltageCA}
                        unit="V"
                        className="left-[60px] top-[200px]"
                    />

                    {/* Current */}

                    <MetricCard
                        title="Current C"
                        value={CurrentC}
                        unit="A"
                        className="left-[370px] top-[30px]"
                    />

                    <MetricCard
                        title="Current B"
                        value={CurrentB}
                        unit="A"
                        className="left-[370px] top-[180px]"
                    />

                    <MetricCard
                        title="Current A"
                        value={CurrentA}
                        unit="A"
                        className="left-[370px] top-[350px]"
                    />

                    <MetricCard
                        title="Current N"
                        value={CurrentN}
                        unit="A"
                        className="left-[370px] top-[570px]"
                    />

                    {/* Power */}

                    <MetricCard
                        title="Active Power"
                        value={TotalActivePowerKW}
                        unit="kW"
                        className="left-[560px] top-[30px] w-[180px]"
                    />

                    <MetricCard
                        title="Reactive Power"
                        value={TotalReactivePowerKVAR}
                        unit="kVAR"
                        className="left-[560px] top-[180px] w-[180px]"
                    />

                    <MetricCard
                        title="Apparent Power"
                        value={TotalApparentPowerKVA}
                        unit="kVA"
                        className="left-[560px] top-[350px] w-[180px]"
                    />

                    {/* <MetricCard
                        title="Power Factor"
                        value={PowerFactor}
                        unit=""
                        className="left-[560px] top-[610px] w-[180px]"
                    /> */}

                    {/* Phase-Neutral */}

                    <MetricCard
                        title="Voltage AN"
                        value={VoltageAN}
                        unit="V"
                        className="left-[850px] top-[460px]"
                    />

                    <MetricCard
                        title="Voltage BN"
                        value={VoltageBN}
                        unit="V"
                        className="left-[1000px] top-[460px]"
                    />

                    <MetricCard
                        title="Voltage CN"
                        value={VoltageCN}
                        unit="V"
                        className="left-[1150px] top-[460px]"
                    />

                    {/* Right panel */}

                    <div className="absolute right-5 top-2 w-[220px]">

                        <div className="bg-slate-50 rounded-xl p-4 space-y-4">

                            <MetricCard
                                absolute={false}
                                title="Frequency"
                                value={FrequencyHz}
                                unit="Hz"
                                className="static w-full"
                            />

                            <MetricCard
                                absolute={false}
                                title="Power Factor"
                                value={PowerFactor}
                                unit=""
                                className="static w-full"
                            />

                            <MetricCard
                                absolute={false}
                                title="V Average"
                                value={voltageAverage.toFixed(1)}
                                unit="V"
                                className="static w-full"
                            />

                            <MetricCard
                                absolute={false}
                                title="V Unbal Worst"
                                value={`${voltageUnbalanceWorst.toFixed(2)}%`}
                                unit=""
                                className="static w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        // </DashboardLayout>
    )
}

export default DeviceDetailsPage;